import { useMemo, useState, useEffect } from "react"; // imported useEffect
import { useNavigate } from "react-router-dom";
import {
  ServiceRequestVM,
  ServiceRequestService,
  NotesEntityTypesEnum,
  DocumentService,
  DocumentEntityTypesEnum,
  DocumentTypesEnum,
  DocumentVM,
  ServiceRequestStatusesEnum,
} from "@propertelligent/client-api";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import notesIcon from "../../assets/componentsIcons/dropdown/doc.svg";
import ServiceIcon from "../../components/common/serviceTypeImage";
import CalenderIcon from "../../assets/svgs/calenderIcon";
import InfoIcon from "../../assets/svgs/infoIcons/infoIcon";
import TenantIcon from "../../assets/svgs/tenantIcons/tenantIcon";
import UserIcon from "../../assets/svgs/unitIcons/userIcon";
import LargeTooltip from "../tooltips/large-tooltip";
import VerificationProgress from "../listings/listing-progressbar/listing-progressbar";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import AddNotesForm from "../notes/AddNotesForm";
import EditServiceRequestForm from "../serviceRequest/EditServiceRequestForm";
import VerifyServiceRequestForm from "../serviceRequest/VerifyServiceRequestForm";
import apiHelper from "../../utils/apiHelper";
import DateHelper from "../../utils/DateHelper";
import { useLeaseInfoTooltip } from "../../hooks/useLeaseInfoTooltip";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { usePermissions } from "../../hooks/usePermissions";

interface ServiceRequestCardItemProps {
  listingData: ServiceRequestVM;
  className?: string;
  IsFooter?: boolean;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onAddNote?: (id: number) => void;
  onVerify?: (id: Number) => void;
  fetchData?: (page: number) => void;
  userRole?: "Landlord" | "Tenant" | "Contractor";
  isDetailPage?: boolean;
  onServiceRequestDeleted?: () => void;
  onServiceRequestVerified?: () => void;
  disableNavigation?: boolean;
  // True when this card is rendered inside a Property/Unit/Tenant/Contractor's own
  // embedded Service Requests tab -- threaded down to EditServiceRequestForm so its
  // Property/Unit fields stay locked there (bug/sr-edit-unlock).
  isEntity?: boolean;
}

interface DisplayImage {
  id: string;
  src: string;
  name: string;
  documentId?: number;
}

const ServiceRequestCardItem: React.FC<ServiceRequestCardItemProps> = ({
  listingData,
  className = "",
  IsFooter = true,
  onEdit,
  onDelete,
  onAddNote,
  onVerify,
  fetchData,
  userRole = "Landlord",
  isDetailPage = false,
  onServiceRequestDeleted,
  onServiceRequestVerified,
  disableNavigation = false,
  isEntity = false,
}) => {
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    description: string;
    position: { top: number; left: number };
    title?: string;
  }>({
    isOpen: false,
    description: "",
    position: { top: 0, left: 0 },
    title: "Description",
  });
  const {
    handleTenantInfoClick: openTenantTooltip,
    handleCloseTooltip: handleCloseTenantTooltip,
    tooltipState: tenantTooltipState,
  } = useLeaseInfoTooltip();
  const [isNoteModalOpen, setNoteModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isVerifyModalOpen, setVerifyModalOpen] = useState(false);
  const [editServiceRequest, setEditServiceRequest] =
    useState<ServiceRequestVM | null>(null);
  const [verifyServiceRequestId, setVerifyServiceRequestId] = useState<number | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // States for Lightbox
  const [isImagePopupOpen, setImagePopupOpen] = useState(false);
  const [images, setImages] = useState<DisplayImage[]>([]);
  const [isLoadingImages, setLoadingImages] = useState(false);

  const navigate = useNavigate();
  const { canWrite } = usePermissions();

  // New Effect: Clear old images whenever the listing ID changes
  useEffect(() => {
    setImages([]);
  }, [listingData.id]);

  const isTenant = userRole === "Tenant";
  const isContractor = userRole === "Contractor";

  const menuItems = useMemo(() => {
    const items = [];

    // Define the disabled condition
    const isDisabled = listingData.serviceRequestStatus !== ServiceRequestStatusesEnum.New;

    if (canWrite("ServiceRequest") || isTenant) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: async (id: number) => {
          if (onEdit) return onEdit(id);

          try {
            const response = await apiHelper(
              ServiceRequestService.getApiServiceRequest1,
              { showNotification: false },
              id
            );
            if (response?.data?.success) {
              setEditServiceRequest(response.data);
              setEditModalOpen(true);
            }
          } catch (err) {
            console.error("Error fetching service request:", err);
          }
        },
        disabled: isDisabled,
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (id: number) => {
          if (onDelete) return onDelete(id);
          setShowConfirmation(String(id));
        },
        isDivider: false,
        isDestructive: true,
      });

      items.push({ isDivider: true });
    }

    // CONDITIONAL ITEMS for Tenant
    if (
      isTenant &&
      listingData.formatted?.ServiceRequestStatus === "Verification Pending"
    ) {
      items.push({
        icon: notesIcon,
        label: "Mark As Verified",
        action: (id: number) => {
          if (onVerify) return onVerify(id);

          // Open the modal immediately and let it fetch its own details with a loader inside --
          // ActionMenu's dropdown closes synchronously on click, so any loading indicator on the
          // menu item itself (or the trigger) is never actually visible to the user.
          setVerifyServiceRequestId(id);
          setVerifyModalOpen(true);
        },
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    // Add Notes: available to Contractors unconditionally (their access is enforced by
    // backend SR-tied ownership, not an org-module permission level), and to non-tenant
    // org-users with write access to the ServiceRequest module.
    if (isContractor || (!isTenant && canWrite("ServiceRequest"))) {
      items.push({
        icon: notesIcon,
        label: "Add Notes",
        action: (id: number) => {
          if (onAddNote) return onAddNote(id);
          setNoteModalOpen(true);
        },
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [
    onEdit,
    onDelete,
    onAddNote,
    onVerify,
    isTenant,
    isContractor,
    listingData,
    canWrite,
  ]);

  const handleViewMoreClick = (
    event: React.MouseEvent<HTMLElement>,
    description: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipState({
      isOpen: true,
      description,
      position: {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      },
    });
  };

  const handleCloseTooltip = () => {
    setTooltipState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDeleteServiceRequest = async (id: number) => {
    if (deletingId) return;

    setDeletingId(String(id));
    try {
      const response = await apiHelper(
        ServiceRequestService.deleteApiServiceRequest,
        {
          successMessage: "Service request deleted successfully",
          showNotification: true,
        },
        id
      );
      if (response?.data?.success) {
        setShowConfirmation(null);
        if (fetchData) {
          await fetchData(1);
          if (onServiceRequestDeleted) onServiceRequestDeleted();

          if (isDetailPage) {
            navigate("/servicerequest");
          }
        }
      }
    } catch (err) {
      console.error("Error deleting service request:", err);
      setShowConfirmation(null);
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewPhotosClick = async (entityId: number) => {

    if (images.length > 0) {
      setImagePopupOpen(true);
      return;
    }

    if (isLoadingImages) return;

    setLoadingImages(true);

    try {
      const response = await apiHelper(
        DocumentService.getApiDocumentList,
        { showNotification: false },
        entityId,
        DocumentEntityTypesEnum.ServiceRequest,
        DocumentTypesEnum.BeforeImage
      );

      if (response?.data?.success && response.data.success.length > 0) {
        const documentArray: DocumentVM[] = response.data.success;
        const imagePromises = documentArray
          .filter(
            (doc) =>
              doc.id &&
              doc.fileName?.toLowerCase().match(/\.(png|jpg|jpeg|gif)$/)
          )
          .map(async (doc) => {
            try {
              const base64Response = await apiHelper(
                DocumentService.getApiDocumentBase641,
                { showNotification: false },
                doc.id!
              );
              if (base64Response?.data?.success) {
                return {
                  id: `api-${doc.id}`,
                  src: `data:image/png;base64,${base64Response.data.success}`,
                  name: doc.fileName || `document-${doc.id}`,
                  documentId: doc.id,
                };
              }
            } catch (error) {
              console.error(
                `Error fetching base64 for document ${doc.id}:`,
                error
              );
            }
            return null;
          });

        const fetchedImages = (await Promise.all(imagePromises)).filter(
          (img) => img !== null
        ) as DisplayImage[];

        setImages(fetchedImages);

        if (fetchedImages.length > 0) {
          setImagePopupOpen(true);
        }
      }
    } catch (err) {
      console.error("Failed to fetch documents list:", err);
    } finally {
      setLoadingImages(false);
    }
  };


  interface CostDetails {
    display: string;
    raw: string;
    compareFormatted: string;
  }

  const getCostDetails = (): CostDetails | null => {
    const { TotalCost, TotalCostRaw, EstimatedCost, EstimatedCostRaw, MinEstimateCost, MinEstimateCostRaw, MaxEstimateCost, MaxEstimateCostRaw } = listingData.formatted;

    if (TotalCost && parseFloat(TotalCost) > 0) {
      return {
        display: `Actual Cost: [[currency]]${TotalCost}`,
        raw: TotalCostRaw,
        compareFormatted: TotalCost
      };
    } else if (EstimatedCost && parseFloat(EstimatedCost) > 0) {
      return {
        display: `Estimated Cost: [[currency]]${EstimatedCost}`,
        raw: EstimatedCostRaw,
        compareFormatted: EstimatedCost
      };
    } else if (
      MinEstimateCost &&
      MaxEstimateCost &&
      parseFloat(MinEstimateCost) > 0 &&
      parseFloat(MaxEstimateCost) > 0
    ) {
      return {
        display: `Estimated Range: [[currency]]${MinEstimateCost} - [[currency]]${MaxEstimateCost}`,
        raw: `${MinEstimateCostRaw} - ${MaxEstimateCostRaw}`,
        compareFormatted: `${MinEstimateCost} - ${MaxEstimateCost}`
      };
    }
    return null;
  };

  const contractorLabel = `Contractor: ${listingData.assignedTo}`;
  const tenantLabel =
    listingData.tenantInfo?.firstName && listingData.tenantInfo?.lastName
      ? `Tenant: ${listingData.tenantInfo.firstName} ${listingData.tenantInfo.lastName}`
      : null;

  return (
    <>
      <LargeTooltip
        isOpen={tooltipState.isOpen}
        onClose={handleCloseTooltip}
        description={tooltipState.description}
        position={tooltipState.position}
        title={tooltipState.title}
      />

      <div className={`listing-card service-request ${className}`}
        key={listingData.id}
      >
        {/* Top section */}
        <div
          className="listing-link"
          style={{
            textDecoration: "none",
            color: "inherit",
            cursor: disableNavigation || isTenant || isContractor ? "auto" : "pointer",
          }}
          onClick={() => {
            if (!disableNavigation && !isTenant && !isContractor) {
              navigate(`/servicerequest/servicerequestdetails/${listingData.id}`);
            }
          }}
        >
          <div className={`listing-card-header ${className}`}>
            <div className="listing-image-container">
              <ServiceIcon serviceType={listingData?.formatted?.ServiceType} />
            </div>
            <div className="listing-details">
              <div className="listing-info-container">
                <div className="listing-info">
                  <div className="d-flex align-items-center listing-info-center">
                    <h3 className="listing-address">
                      {listingData?.formatted?.ServiceType} -{" "}
                      {listingData.formatted.ServiceSubType}
                    </h3>
                  </div>
                  <p className="listing-type mt-2">
                    {listingData.streetAddress}
                  </p>
                  <p className="listing-type mt-2">
                    {listingData.description.length > 30
                      ? `${listingData.description.slice(0, 30)}...`
                      : listingData.description}
                    {listingData.description.length > 30 && (
                      <span
                        className="listing-viewmore mx-2 ml-1 text-blue-500 underline cursor-pointer"
                        onClick={(e) =>
                          handleViewMoreClick(e, listingData.description)
                        }
                      >
                        View more
                      </span>
                    )}
                  </p>
                </div>
                <div className="onboarding-listing-progress">
                  <div className="listing-progress-wrapper">
                    <VerificationProgress
                      progress={listingData?.percentCompleted || 0}
                      progressText="Progress"
                      progressStatus={
                        listingData?.formatted?.ServiceRequestStatus || ""
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {IsFooter && (
          <>
            <div className="separator-horizontal" />
            <div className="listing-card-footer">
              <div className="listing-stats">
                {listingData.requestDate && (
                  <div className="stat">
                    <CalenderIcon
                      className="stat-icon"
                      width="24"
                      height="24"
                    />
                    <span className="stat-text">
                      {"Created on: " + DateHelper.convertUTCtoDateTimeSimple(String(listingData.requestDate))}
                    </span>
                  </div>
                )}
                {!isTenant && listingData.assignedTo && (
                  <div className="stat stat--truncate">
                    <TenantIcon className="stat-icon" width="24" height="24" />
                    <span className="stat-text">
                      <span className="stat-text-ellipsis" title={contractorLabel}>
                        {contractorLabel}
                      </span>
                    </span>
                  </div>
                )}
                {tenantLabel && (
                  <div className="stat stat--truncate">
                    <UserIcon className="stat-icon" width="24" height="24" />
                    <span className="stat-text">
                      <span className="stat-text-ellipsis" title={tenantLabel}>
                        {tenantLabel}
                      </span>
                      <div className="info-tooltip-wrapper">
                        <span
                          onClick={(e) => {
                            if (!listingData.tenantInfo) return;
                            const aEvent = e as unknown as React.MouseEvent<HTMLAnchorElement>;
                            openTenantTooltip(aEvent, listingData.tenantInfo as any);
                          }}
                          className="clickable"
                          style={{ marginLeft: "5px" }}
                        >
                          {!isTenant && <InfoIcon className="color-white-icon" />}
                        </span>

                        {tenantTooltipState.isOpen && (
                          <LargeTooltip
                            isOpen={tenantTooltipState.isOpen}
                            onClose={handleCloseTenantTooltip}
                            description={tenantTooltipState.description}
                            position={tenantTooltipState.position}
                            title={tenantTooltipState.title || "Tenant Contact Info"}
                            type="tenant"
                            placement={tenantTooltipState.position?.placement}
                          />
                        )}
                      </div>
                    </span>
                  </div>
                )}

                {listingData && listingData.hasPhotos && (
                  <div className="stat">
                    <span className="stat-text">
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewPhotosClick(listingData.id!);
                        }}
                        className={`listing-viewmore ml-1 text-blue-500 underline ${isLoadingImages ? 'opacity-50 cursor-wait' : ''}`}
                      >
                        {isLoadingImages ? "Loading..." : "View Photo(s)"}
                      </a>
                    </span>
                  </div>
                )}

              </div>
              <div className="footer-actions">
                {!isTenant && <div className="highlight-value">
                  {(() => {
                    const costDetails = getCostDetails();
                    if (costDetails) {
                      return (
                        <CurrencyTooltip
                          content={costDetails.raw}
                          formatted={costDetails.compareFormatted}
                        >
                          <span className="highlight-text">
                            {costDetails.display}
                          </span>
                        </CurrencyTooltip>
                      );
                    }
                    return null;
                  })()}
                </div>}
                {menuItems.length > 0 && (
                  <ActionMenu
                    menuItems={menuItems.map((item) => ({
                      ...item,
                      action: () => item.action && item.action(listingData.id!),
                    }))}
                    trigger={
                      <button className="action-button">
                        <img
                          src={dropdownIcon}
                          alt="More options"
                          width={20}
                          height={20}
                          className="action-icon"
                        />
                      </button>
                    }
                  />
                )}
              </div>
            </div>
          </>
        )}

        {
          isNoteModalOpen && (
            <AddNotesForm
              entityId={listingData.id!}
              entityTypeId={NotesEntityTypesEnum.ServiceRequest}
              isModalOpen={isNoteModalOpen}
              setModalOpen={setNoteModalOpen}
            />
          )
        }

        {
          isEditModalOpen && (
            <EditServiceRequestForm
              response={editServiceRequest}
              isModalOpen={isEditModalOpen}
              setModalOpen={setEditModalOpen}
              fetchData={fetchData}
              isEntity={isEntity}
            />
          )
        }


        {showConfirmation === String(listingData.id) && (
          <ConfirmationPopup
            isOpen={true}
            title="Delete Service Request"
            message="Are you sure you want to delete this service request?"
            primaryButtonLabel="Delete"
            secondaryButtonLabel="Cancel"
            onPrimaryAction={() => handleDeleteServiceRequest(listingData.id!)}
            onSecondaryAction={() => setShowConfirmation(null)}
            onClose={() => setShowConfirmation(null)}
            isPrimaryDestructive={true}
            isLoading={deletingId === String(listingData.id)}
            loadingLabel="Deleting..."
          />
        )}

        {
          isVerifyModalOpen && (
            <VerifyServiceRequestForm
              serviceRequestId={verifyServiceRequestId}
              isModalOpen={isVerifyModalOpen}
              setModalOpen={setVerifyModalOpen}
              fetchData={fetchData}
              onServiceRequestVerified={onServiceRequestVerified}
            />
          )
        }
      </div>

      <Lightbox
        open={isImagePopupOpen}
        close={() => setImagePopupOpen(false)}
        slides={images.map(img => ({ src: img.src, alt: img.name }))}
        plugins={[Thumbnails]}
        thumbnails={{ position: "bottom" }}
      />
    </>
  );
};

export default ServiceRequestCardItem;